import { useState, useCallback, useMemo } from 'react';

import { defaultApi } from '../../../../../../apis';
import {
  ConnectorApiKeyAuth,
  ConnectorApiKeyAuthTypeEnum,
  ConnectorModel,
  ELMA365AppModelInput,
  ELMA365ConnectorSettingsOutputTypeEnum,
  ELMA365NSModel,
} from '../../../../../../../gamma-api';
import { hasObjectId } from '../../../../../../utils/idUtils';
import { EMPTY_CONNECTOR } from '../../../../../../components/ConnectorModal/const';

import { joinNamespaceAndCode } from './ParametersMain';

type ELMA365AppResult = {
  value: string;
  key: string;
  namespaceCode: string;
  namespaceName: string;
  code: string;
  name: string;
};

const EMPTY_KEY = '';

export const useElma365Apps = (
  connectorId: string,
  temporal?: boolean,
  onError?: (error: unknown) => void
): {
  nss: Map<string, ELMA365NSModel>;
  apps: ELMA365AppModelInput[];
  searchApps: (search?: string) => Promise<ELMA365AppResult[]>;
} => {
  const [nss, setNss] = useState<Map<string, ELMA365NSModel>>(new Map());
  const [apps, setApps] = useState<ELMA365AppModelInput[]>([]);

  const connectorCache = useMemo((): Map<string, Promise<ConnectorModel>> => {
    return new Map();
  }, []);

  const namespacesCache = useMemo((): Map<string, Promise<Map<string, ELMA365NSModel>>> => {
    return new Map();
  }, [connectorId]);

  const applicationsCache = useMemo((): Map<string, Promise<ELMA365AppModelInput[]>> => {
    return new Map();
  }, [connectorId]);

  const resultsCache = useMemo((): Map<string, Promise<ELMA365AppResult[]>> => {
    return new Map();
  }, [connectorId]);

  const isValidConnector = (connector: ConnectorModel): boolean => {
    if (!hasObjectId(connector.connector_id)) {
      return false;
    }

    const settings = connector.settings;
    if (settings.type !== ELMA365ConnectorSettingsOutputTypeEnum.Elma365) {
      return false;
    }

    const auth = settings.apis.web.auth;
    return auth.type === ConnectorApiKeyAuthTypeEnum.ApiKey;
  };

  const fetchConnector = async (): Promise<ConnectorModel> => {
    if (!hasObjectId(connectorId)) {
      return EMPTY_CONNECTOR;
    }

    try {
      const response = await defaultApi.getOneConnector(connectorId);

      if (temporal) {
        connectorCache.delete(connectorId);
      }

      return response.data;
    } catch (err) {
      onError?.(err);
      return EMPTY_CONNECTOR;
    }
  };

  const fetchNamespaces = async (connector: ConnectorModel): Promise<Map<string, ELMA365NSModel>> => {
    if (!isValidConnector(connector)) {
      return new Map();
    }

    try {
      const response = await defaultApi.searchElma365NssElma365NsGet(
        connector.settings.apis.web.url,
        (connector.settings.apis.web.auth as ConnectorApiKeyAuth).token
      );

      if (temporal) {
        namespacesCache.delete(EMPTY_KEY);
      }

      return new Map(response.data.map((namespace) => [namespace.code, namespace]));
    } catch (err) {
      onError?.(err);
      return new Map();
    }
  };

  const fetchApplications = async (connector: ConnectorModel, search?: string): Promise<ELMA365AppModelInput[]> => {
    if (!isValidConnector(connector)) {
      return [];
    }

    try {
      const response = await defaultApi.searchElma365AppsElma365AppGet(
        connector.settings.apis.web.url,
        (connector.settings.apis.web.auth as ConnectorApiKeyAuth).token,
        search
      );

      if (temporal) {
        const applicationsKey = search || EMPTY_KEY;
        applicationsCache.delete(applicationsKey);
      }

      return response.data;
    } catch (err) {
      onError?.(err);
      return [];
    }
  };

  const fetchResults = async (search?: string) => {
    const resultsKey = search || EMPTY_KEY;

    if (!hasObjectId(connectorId)) {
      setNss(new Map());
      setApps([]);
      temporal && resultsCache.delete(resultsKey);
      return [];
    }

    let connectorPromise = connectorCache.get(connectorId);
    if (!connectorPromise) {
      connectorPromise = fetchConnector();
      connectorCache.set(connectorId, connectorPromise);
    }
    const connector = await connectorPromise;

    let namespacesPromise = namespacesCache.get(EMPTY_KEY);
    if (!namespacesPromise) {
      namespacesPromise = fetchNamespaces(connector);
      namespacesCache.set(EMPTY_KEY, namespacesPromise);
    }
    const namespaces = await namespacesPromise;

    const applicationsKey = search || EMPTY_KEY;
    let applicationsPromise = applicationsCache.get(applicationsKey);
    if (!applicationsPromise) {
      applicationsPromise = fetchApplications(connector, search);
      applicationsCache.set(applicationsKey, applicationsPromise);
    }
    const applications = await applicationsPromise;

    setNss(namespaces);
    setApps(applications);
    temporal && resultsCache.delete(resultsKey);
    return applications.map((application) => {
      const pair = joinNamespaceAndCode(application.namespace, application.code);
      return {
        value: pair,
        key: pair,
        namespaceCode: application.namespace,
        namespaceName: namespaces.get(application.namespace)?.name || application.namespace,
        code: application.code,
        name: application.name,
      };
    });
  };

  const searchApps = useCallback(
    async (search?: string) => {
      const resultsKey = search || EMPTY_KEY;
      let resultsPromise = resultsCache.get(resultsKey);
      if (!resultsPromise) {
        resultsPromise = fetchResults(search);
        resultsCache.set(resultsKey, resultsPromise);
      }
      return await resultsPromise;
    },
    [connectorId, onError]
  );

  return { nss, apps, searchApps };
};
